import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse, SearchMode } from '@boma/client-shared';
import {
  IDepartmentResponse,
  IListing,
  IProduct,
} from '@boma/supplier-portal/shared/entities';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  searchProducts(
    operatingEntityId: string,
    query: string = '',
    page: string = '1',
    perPage: string = '25',
    categoryIds: string[] = [],
    filters: { key: string; value: string }[] = [],
    operatingLocationId: string = null,
    supplierId: string = null,
    brandId: string = null,
    searchMode: SearchMode = SearchMode.None,
    includeAll: boolean = false,
    definitionIds: string[] = []
  ) {
    //console.log(searchMode);
    const queryParams = [];
    let urlQuery = '';

    if (page) {
      queryParams.push(`page=${page}`);
    }
    if (perPage) {
      queryParams.push(`perPage=${perPage}`);
    }
    if (query) {
      queryParams.push(`query=${query}`);
    }
    if (filters && filters.length) {
      const filterValue = filters.map((f) => `${f.key}:${f.value}`).join('|');
      queryParams.push(`filters=${filterValue}`);
    }
    if (operatingLocationId) {
      queryParams.push(`operatingLocationId=${operatingLocationId}`);
    }
    if (operatingEntityId) {
      queryParams.push(`operatingEntityId=${operatingEntityId}`);
    }
    if (supplierId) {
      queryParams.push(`supplierId=${supplierId}`);
    }
    if (brandId) {
      queryParams.push(`brandId=${brandId}`);
    }
    if (searchMode) {
      queryParams.push(`searchMode=${searchMode}`);
    }
    if (queryParams.length > 0) {
      urlQuery = `?${queryParams.join('&')}`;
    }

    return this.httpClient.post<IPaginatedResponse<IProduct>>(
      `${this.base}api/v${this.version}/Search/Products${urlQuery}`,
      { categoryIds: categoryIds, definitionIds: definitionIds }
    );
  }

  //TODO whats the difference between this and search product uses same endpoint
  //Maybe just better parameter handeling?
  searchAllProductsForSaleOnGlobalCatalogue(
    parameters,
    categoryIds = [],
    definitionIds = []
  ) {
    const selectedCategoryIds = categoryIds;
    const selectedDefinitionIds = categoryIds;

    const queryParams = [];
    let urlQuery = '';

    if (parameters.page) {
      queryParams.push(`page=${parameters.page}`);
    }
    if (parameters.perPage) {
      queryParams.push(`perPage=${parameters.perPage}`);
    }
    if (parameters.query) {
      queryParams.push(`query=${parameters.query}`);
    }
    if (parameters.filters && parameters.filters.length) {
      const filterValue = parameters.filters
        .map((f) => `${f.key}:${f.value}`)
        .join('|');
      queryParams.push(`filters=${filterValue}`);
    }
    if (parameters.operatingLocationId) {
      queryParams.push(`operatingLocationId=${parameters.operatingLocationId}`);
    }
    if (parameters.operatingEntityId) {
      queryParams.push(`operatingEntityId=${parameters.operatingEntityId}`);
    }
    if (parameters.supplierId) {
      queryParams.push(`supplierId=${parameters.supplierId}`);
    }
    if (parameters.brandId) {
      queryParams.push(`brandId=${parameters.brandId}`);
    }
    if (parameters.searchMode) {
      queryParams.push(`searchMode=${parameters.searchMode}`);
    }
    if (queryParams.length > 0) {
      urlQuery = `?${queryParams.join('&')}`;
    }

    return this.httpClient.post<IPaginatedResponse<IProduct>>(
      `${this.base}api/v${this.version}/Search/Products${urlQuery}`,
      { selectedDefinitionIds, selectedCategoryIds }
    );
  }

  getSuggestions(
    query: string = '',
    categoryId: string = null,
    operatingLocationId: string = null,
    operatingEntityId: string = null
  ) {
    const queryParams = [];

    if (query) {
      queryParams.push(`query=${query}`);
    }
    if (categoryId) {
      queryParams.push(`categoryId=${categoryId}`);
    }
    if (operatingLocationId) {
      queryParams.push(`operatingLocationId=${operatingLocationId}`);
    }
    if (operatingEntityId) {
      queryParams.push(`operatingEntityId=${operatingEntityId}`);
    }
    const urlQuery = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return this.httpClient.get<any>(
      `${this.base}api/v${this.version}/Search/Products/Suggestions${urlQuery}`
    );
  }

  searchInformalListings(
    productId: string = null,
    listingType: string = null, // Buying Selling
    maximumDistance: number = null,
    operatingLocationId: string = null,
    operatingEntityId: string = null,
    latitude: number = null,
    longitude: number = null,
    page: number = 1,
    perPage: number = 25
  ) {
    let queryParams = [];
    let urlQuery = '';

    if (page) {
      queryParams.push(`page=${page}`);
    }
    if (perPage) {
      queryParams.push(`perPage=${perPage}`);
    }
    if (productId) {
      queryParams.push(`productId=${productId}`);
    }
    if (operatingLocationId) {
      queryParams.push(`operatingLocationId=${operatingLocationId}`);
    }
    if (operatingEntityId) {
      queryParams.push(`operatingEntityId=${operatingEntityId}`);
    }
    if (latitude) {
      queryParams.push(`latitude=${latitude}`);
    }
    if (longitude) {
      queryParams.push(`longitude=${longitude}`);
    }
    if (listingType) {
      queryParams.push(`type=${listingType}`);
    }
    if (maximumDistance) {
      queryParams.push(`maximumDistance=${maximumDistance}`);
    }

    if (queryParams.length > 0) {
      urlQuery = `?${queryParams.join('&')}`;
    }

    return this.httpClient.get<IPaginatedResponse<IListing>>(
      `${this.base}api/v${this.version}/Search/InformalListings${urlQuery}`
    );
  }

  getDepartments(query: string = '') {
    const urlQuery = query ? `?query=${query}` : '';
    return this.httpClient.get<IDepartmentResponse[]>(
      `${this.base}api/v${this.version}/Departments${urlQuery}`
    );
  }
}
